import {
  createMemoryHistory,
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
} from 'vue-router';
import { useSettingsStore } from './stores/settings';
import { useTracker } from './composables/tracker';
import { useMeStore } from './stores/me';
import fetch from './modules/fetch';
import { useMetaTitle } from './composables/seo';

const Home = async () => import('./pages/HomePage.vue');

const QuoteUpload = async () => import('./pages/quote/Upload.vue');
const QuoteEdit = async () => import('./pages/QuoteEdit.vue');
const QuoteProviders = async () => import('./pages/quote/Providers.vue');
const Quote3DDrive = async () => import('./pages/quote/DrivePage.vue');
const QuoteSummary = async () => import('./pages/QuoteSummary.vue');

const Showroom = async () => import('./pages/Showroom.vue');

const MyPrints = async () => import('./pages/MyPrints.vue');
const MyOrders = async () => import('./pages/MyOrders.vue');

const Addin = async () => import('./pages/Addin.vue');

const Bidding = async () => import('./pages/Bidding.vue');
const Payment = async () => import('./pages/Payment.vue');

const FAQ = async () => import('./pages/faq/IndexPage.vue');
const FAQArticles = async () => import('./pages/faq/FaqArticlesPage.vue');

const Processes = async () => import('./pages/processes/IndexPage.vue');
const ProcessesArticles = async () => import('./pages/processes/ProcessesArticlesPage.vue');

const Materials = async () => import('./pages/materials/IndexPage.vue');
const MaterialsArticles = async () => import('./pages/materials/MaterialsArticlesPage.vue');

const LabCreation = async () => import('./pages/LabCreation.vue');

const LabOverview = async () => import('./pages/lab/LabOverview.vue');
const LabManagement = async () => import('./pages/lab/LabManagement.vue');
const LabManagementOperator = async () => import('./pages/lab/LabManagementOperator.vue');

const LabValidation = async () => import('./pages/operator/LabValidation.vue');
const TermsOfUse = async () => import('./pages/operator/TermsOfUse.vue');
const SearchOrders = async () => import('./pages/operator/SearchOrders.vue');

const ReferentialAdmin = async () => import('./pages/operator/ReferentialAdmin.vue');
const SellerStats = async () => import('./pages/SellerStats.vue');
const BecomeAPartner = async () => import('./pages/BecomeAPartner.vue');
const SiteMap = async () => import('./pages/SiteMap.vue');

const Error = async () => import('./pages/ErrorPage.vue');

// OTHERS SERVICES
const CnB = async () => import('./pages/CnB.vue');

const QUERY_PARAMS_TO_KEEP = [
  'source',
  'connect',
  'esid',
  'test',
  'lockedapp',
  'PMP',
  'screenshots',
];
const redirectTo3DSFaq = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.location.href = 'https://www.3ds.com/make/guide/faq/';
};

const redirectTo3DSDotCom = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.location.href = 'https://www.3ds.com';
};

const tracker = useTracker();
const FAQPages = [
  {
    path: '/faq',
    component: FAQ,
    name: 'FAQ',
    redirect: redirectTo3DSFaq,
    meta: { redirect: 'https://www.3ds.com/make/guide/faq/' },
  },
  {
    path: '/faq/:id',
    component: FAQArticles,
    name: 'FAQArticles',
    redirect: redirectTo3DSFaq,
    meta: { redirect: 'https://www.3ds.com/make/guide/faq/' },
  },
  {
    path: '/processes',
    component: Processes,
    name: 'WikiProcesses',
    meta: {
      title: 'Make - Wiki of processes in 3DExperience',
      description:
        '3DEXPERIENCE Make Marketplace: Find informations about processes of 3DPrintings',
      keywords:
        '3dprinting processes, manufacturing, learn manufacturing, learn 3dprinting, learn 3dprinting online, 3d processes, metal 3D printing, additive processes',
    },
  },
  { path: '/processes/:id', component: ProcessesArticles, name: 'ProcessesArticles' },
  {
    path: '/materials',
    component: Materials,
    name: 'WikiMaterials',
    meta: {
      title: 'Make - Wiki of materials in 3DExperience',
      description:
        '3DEXPERIENCE Make Marketplace: Find informations about materials of 3DPrintings',
      keywords:
        '3dprinting materials, manufacturing, about 3dprinting, manufacturing materials, 3dprinting metal, 3dprinting plastic',
    },
  },
  { path: '/materials/:id', component: MaterialsArticles, name: 'MaterialsArticles' },
];

const labManagementRoutes = [
  {
    path: '/lab-management',
    component: LabManagement,
    name: 'LabManagement',
    meta: { requireAuth: true },
    /* TO UNCOMMENT TO BLOCK LAB CREATION IF THERE IS ALREADY ONE CREATED
		async beforeEnter(to: RouteLocationNormalized) {
			const me = useMeStore();

			if (!me.isMeFetched) {
				await me.fetchMe();
				await me.fetchMeShops();
			}

			if (me.currentShop) {
				return router.push({name: 'LabManagementInfo', params: {id: me.currentShop}});
			}
		}, */
  },
  {
    path: '/lab-management/:id/info',
    component: LabManagement,
    name: 'LabManagementInfo',
    meta: { requireAuth: true },
  },
  {
    path: '/lab-management/:id/machines',
    component: LabManagement,
    name: 'LabManagementMachines',
    meta: { requireAuth: true },
  },
  {
    path: '/lab-management/:id/machine',
    component: LabManagement,
    name: 'LabManagementNewMachine',
    meta: { requireAuth: true },
  },
  {
    path: '/lab-management/:id/machine/:machineId',
    component: LabManagement,
    name: 'LabManagementMachine',
    meta: { requireAuth: true },
  },
  {
    path: '/lab-management/:id/finishes',
    component: LabManagement,
    name: 'LabManagementFinishes',
    meta: { requireAuth: true },
  },
  {
    path: '/lab-management/:id/services',
    component: LabManagement,
    name: 'LabManagementServices',
    meta: { requireAuth: true },
  },
  {
    path: '/lab-management/:id/pricing',
    component: LabManagement,
    name: 'LabManagementPricing',
    meta: { requireAuth: true },
  },
  {
    path: '/lab-management/:id/pricing/additive',
    component: LabManagement,
    name: 'LabManagementPricingAdditive',
    meta: { requireAuth: true },
  },
  {
    path: '/lab-management/:id/pricing/finish',
    component: LabManagement,
    name: 'LabManagementPricingFinish',
    meta: { requireAuth: true },
  },
  {
    path: '/lab-management/:id/pricing/shipping',
    component: LabManagement,
    name: 'LabManagementShipping',
    meta: { requireAuth: true },
  },
  {
    path: '/lab-management/:id/showroom',
    component: LabManagement,
    name: 'LabManagementShowroom',
    meta: { requireAuth: true },
  },
  {
    path: '/lab-management/:id/members',
    component: LabManagement,
    name: 'LabManagementMembers',
    meta: { requireAuth: true },
  },
  {
    path: '/lab-management/:id/payment',
    component: LabManagement,
    name: 'LabManagementPayment',
    meta: { requireAuth: true },
  },
  {
    path: '/lab-management/:id/submit',
    component: LabManagement,
    name: 'LabManagementSubmit',
    meta: { requireAuth: true },
  },
  {
    path: '/lab-management/:id/submit/success',
    component: LabManagement,
    name: 'LabManagementSubmitSuccess',
    meta: { requireAuth: true },
  },
];

const labManagementOperatorRoutes = [
  {
    path: '/lab-management-operator/:id/showroom',
    component: LabManagementOperator,
    name: 'LabManagementOperatorShowroom',
    meta: { requireAuth: true, requireOperator: true },
  },
  {
    path: '/lab-management-operator/:id/documents',
    component: LabManagementOperator,
    name: 'LabManagementOperatorDocuments',
    meta: { requireAuth: true, requireOperator: true },
  },
  {
    path: '/lab-management-operator/:id/info',
    component: LabManagementOperator,
    name: 'LabManagementOperatorInfo',
    meta: { requireAuth: true, requireOperator: true },
  },
  {
    path: '/lab-management-operator/:id/machines',
    component: LabManagementOperator,
    name: 'LabManagementOperatorMachines',
    meta: { requireAuth: true, requireOperator: true },
  },
  {
    path: '/lab-management-operator/:id/machine/:machineId',
    component: LabManagementOperator,
    name: 'LabManagementOperatorMachine',
    meta: { requireAuth: true, requireOperator: true },
  },
  {
    path: '/lab-management-operator/:id/pricing',
    component: LabManagementOperator,
    name: 'LabManagementOperatorPricing',
    meta: { requireAuth: true, requireOperator: true },
  },
  {
    path: '/lab-management-operator/:id/pricing/additive',
    component: LabManagementOperator,
    name: 'LabManagementOperatorPricingAdditive',
    meta: { requireAuth: true, requireOperator: true },
  },
  {
    path: '/lab-management-operator/:id/pricing/finishes',
    component: LabManagementOperator,
    name: 'LabManagementOperatorPricingFinish',
    meta: { requireAuth: true, requireOperator: true },
  },
  {
    path: '/lab-management-operator/:id/pricing/shipping',
    component: LabManagementOperator,
    name: 'LabManagementOperatorShipping',
    meta: { requireAuth: true, requireOperator: true },
  },
  {
    path: '/lab-management-operator/:id/services',
    component: LabManagementOperator,
    name: 'LabManagementOperatorServices',
    meta: { requireAuth: true, requireOperator: true },
  },
  {
    path: '/lab-management-operator/:id/members',
    component: LabManagementOperator,
    name: 'LabManagementOperatorMembers',
    meta: { requireAuth: true, requireOperator: true },
  },
];

const referentialAdminRoutes = [
  {
    path: '/referential-admin',
    component: ReferentialAdmin,
    name: 'ReferentialAdmin',
    meta: { requireAuth: true, requireOperator: true },
  },
  {
    path: '/referential-admin/material-family',
    component: ReferentialAdmin,
    name: 'ReferentialAdminMaterialFamily',
    meta: { requireAuth: true, requireOperator: true },
  },
  {
    path: '/referential-admin/material-class',
    component: ReferentialAdmin,
    name: 'ReferentialAdminMaterialClass',
    meta: { requireAuth: true, requireOperator: true },
  },
  {
    path: '/referential-admin/material-type',
    component: ReferentialAdmin,
    name: 'ReferentialAdminMaterialType',
    meta: { requireAuth: true, requireOperator: true },
  },
  {
    path: '/referential-admin/material',
    component: ReferentialAdmin,
    name: 'ReferentialAdminMaterial',
    meta: { requireAuth: true, requireOperator: true },
  },
  {
    path: '/referential-admin/process-family',
    component: ReferentialAdmin,
    name: 'ReferentialAdminProcessFamily',
    meta: { requireAuth: true, requireOperator: true },
  },
  {
    path: '/referential-admin/process-type',
    component: ReferentialAdmin,
    name: 'ReferentialAdminProcessType',
    meta: { requireAuth: true, requireOperator: true },
  },
  {
    path: '/referential-admin/machine',
    component: ReferentialAdmin,
    name: 'ReferentialAdminMachine',
    meta: { requireAuth: true, requireOperator: true },
  },
  {
    path: '/referential-admin/process',
    component: ReferentialAdmin,
    name: 'ReferentialAdminProcess',
    meta: { requireAuth: true, requireOperator: true },
  },
  {
    path: '/referential-admin/covering',
    component: ReferentialAdmin,
    name: 'ReferentialAdminCovering',
    meta: { requireAuth: true, requireOperator: true },
  },
];

const operatorRoutes = [
  {
    path: '/lab-validation',
    component: LabValidation,
    name: 'LabValidation',
    meta: { requireAuth: true, requireOperator: true },
  },
  {
    path: '/terms-and-conditions',
    component: TermsOfUse,
    name: 'TermsAndConds',
    meta: { requireAuth: true, requireOperator: true },
  },
  {
    path: '/search-orders',
    component: SearchOrders,
    name: 'SearchOrders',
    meta: { requireAuth: true, requireOperator: true },
  },
];

const orderingRoutes = [
  {
    path: '/quote/upload',
    component: QuoteUpload,
    name: 'QuoteUploadPart',
    meta: {
      title: 'Make - Upload your 3D part to get a quote',
      description:
        '3DEXPERIENCE Make Marketplace: Upload your 3D part to get a quote from a manufacturer',
      keywords:
        '3dprinting order, order 3dprinted parts, 3dprint online order, 3dorder, upload 3d file, quote 3d file, online quote for 3d model, online 3d quote',
    },
  },
  {
    path: '/quote/edit',
    component: QuoteEdit,
    name: 'QuoteEdition',
  },
  {
    path: '/quote/providers',
    component: QuoteProviders,
    name: 'QuoteProviders',
  },
  {
    path: '/quote/summary/shipping',
    component: QuoteSummary,
    name: 'QuoteSummaryShipping',
    meta: { requireAuth: true },
  },
  {
    path: '/quote/summary/success',
    component: QuoteSummary,
    name: 'QuoteSummarySuccess',
    meta: { requireAuth: true },
  },
  {
    path: '/quote/drive',
    component: Quote3DDrive,
    name: 'Quote3DDrive',
    meta: { requireAuth: true },
  },
];

const addinRedirections = [
  { path: '/print/config/explore/', redirect: '/quote/edit' },
  {
    path: '/print/config/explore/documentId/:documentId/documentName/:documentName/tenant/:tenant/source/:source',
    redirect(to: RouteLocationNormalized) {
      return {
        path: '/quote/edit',
        query: {
          documentName: to.params.documentName,
          documentId: to.params.documentId,
          source: to.params.source,
          tenant: to.params.tenant,
        },
      };
    },
  },
  {
    path: '/print/config/explore/documentId/:documentId/documentName/:documentName/source/:source',
    redirect(to: RouteLocationNormalized) {
      return {
        path: '/quote/edit',
        query: {
          documentName: to.params.documentName,
          documentId: to.params.documentId,
          source: to.params.source,
        },
      };
    },
  },
  {
    path: '/print/config/explore/documentId/:documentId/documentName/:documentName/tenant/:tenant',
    redirect(to: RouteLocationNormalized) {
      return {
        path: '/quote/edit',
        query: {
          documentName: to.params.documentName,
          documentId: to.params.documentId,
          tenant: to.params.tenant,
        },
      };
    },
  },
  {
    path: '/print/config/explore/documentId/:documentId/documentName/:documentName',
    redirect(to: RouteLocationNormalized) {
      return {
        path: '/quote/edit',
        query: {
          documentName: to.params.documentName,
          documentId: to.params.documentId,
        },
      };
    },
  },
];

const CnBRoutes = [
  { path: '/cnb', component: CnB, name: 'ClickAndBuy' },
  { path: '/cnb/:pathMatch(.*)*', component: CnB, name: 'ClickAndBuy' },
];

const logout = {
  path: '/logout',
  component: {
    async beforeRouteEnter(_next: any) {
      if (typeof window === 'undefined') {
        return;
      }

      const passportUrl = await fetch.getServiceURL('3DPassport');

      try {
        await fetch.get('/servlet/logout', { service: '3DCompass' });
      } catch (e) {
        console.log(e);
      }

      const url = new URL(window.location.href);

      const redirection = encodeURIComponent(url.origin);
      window.location.href = `${passportUrl}/cas/logout?service=${redirection}`;
    },
  },
};

const routes = [
  {
    path: '/',
    component: Home,
    name: 'Home',
    async beforeEnter(to: RouteLocationNormalized) {
      const isSolidworks = to.query.source === 'solidworks';
      if (isSolidworks) {
        return router.replace({ path: '/welcome', query: { ...to.query } });
      }
    },
    meta: {
      hasFooter: true,
      title: 'Make - 3DEXPERIENCE Marketplace',
      description:
        '3DEXPERIENCE Make Marketplace: Upload your Design , Select your Process, Choose your Manufacturer, Get your quotes and Receive your parts.',
      keywords:
        'Make marketplace, making 3d, 3d, marketplace 3d printer, 3dprinting marketplace, 3dprinter marketplace, dassault systemes 3dexperience, 3dexperience, 3dprinting instant quote, instant quote, 3dprinting service, dassault 3dprinting',
    },
  },
  {
    path: '/welcome',
    component: Home,
    name: 'Welcome',
    /* TO UNCOMMENT IF SSR MANAGE DATA & THE CHECK FOR ISSELLER ON BACKEND SIDE
		WILL IMPROVE THE SEO
		async beforeEnter(to: RouteLocationNormalized) {
			const isSolidworks = to.query.source === 'solidworks';
			 if (!isSolidworks) {
				return router.replace({path: '/', query: {...to.query}});
			}
		}, */
    meta: {
      canonical: '/welome',
      hasFooter: true,
      description:
        '3DEXPERIENCE Make Marketplace: Upload your Design , Select your Process, Choose your Manufacturer, Get your quotes and Receive your parts.',
      keywords:
        'Make marketplace, making 3d, 3d, marketplace 3d printer, 3dprinting marketplace, 3dprinter marketplace, dassault systemes 3dexperience, 3dexperience, 3dprinting instant quote, instant quote, 3dprinting service, dassault 3dprinting',
    },
  },
  {
    path: '/lab-creation',
    component: LabCreation,
    name: 'LabCreation',
    meta: { requireAuth: true },
  },
  {
    path: '/labs',
    component: QuoteProviders,
    name: 'LabsExploration',
    meta: {
      hasFooter: true,
      title: 'Make - Listed 3D experts providers',
      description: '3DEXPERIENCE Make Marketplace: Find services providers for your quotes',
      keywords:
        '3dprinting manufacturers, manufacturers, nearby 3d printing service, local manufacturer, 3dprinting lab, 3dprinter file',
    },
  },
  {
    path: '/my-prints',
    component: MyPrints,
    name: 'MyPrints',
    meta: {
      requireAuth: true,
      title: 'Make - Review and Checkout my prints',
      description: '3DEXPERIENCE Make Marketplace: Find my quotes and discuss with a manufacturer',
      keywords:
        '3dprinting manufacturers, my prints, my-quotes, direct-purchase, price, list, 3dprinting lab, 3dprinter file, 3d quotes list',
    },
  },
  { path: '/lab/:id', component: Showroom, name: 'Showroom' },
  { path: '/store/:id', component: QuoteUpload, name: 'QuoteUploadWithSelectedLab' },
  {
    path: '/received-orders/:id',
    component: MyOrders,
    name: 'MyOrders',
    meta: { requireAuth: true },
  },
  {
    path: '/lab-overview/:id',
    component: LabOverview,
    name: 'LabOverview',
    meta: { requireAuth: true },
  },
  { path: '/stats/:id', component: SellerStats, name: 'SellerStats', meta: { requireAuth: true } },
  { path: '/order/:orderId', component: Bidding, name: 'Bidding', meta: { requireAuth: true } },
  { path: '/payment/:cartId', component: Payment, name: 'Payment', meta: { requireAuth: true } },
  ...orderingRoutes,
  ...labManagementRoutes,
  ...labManagementOperatorRoutes,
  ...operatorRoutes,
  ...referentialAdminRoutes,
  ...FAQPages,
  logout,
  ...CnBRoutes,
  ...addinRedirections,
  {
    path: '/become-a-manufacturing-partner',
    component: BecomeAPartner,
    name: 'BecomeAPartner',
    meta: {
      hasFooter: true,
      title: 'Make - Become a manufacturing partner',
      description:
        '3DEXPERIENCE Make Marketplace: Become a manufacturing partner of Dassault Systemes and start receive quotes',
      keywords:
        '3dprinting service provider, provider dassault, 3dexperience provider, make 3d provider, 3dexperience seller',
    },
  },
  { path: '/addin', component: Addin, name: 'Addin' },
  { path: '/sitemap', component: SiteMap, name: 'SiteMap' },
  { path: '/404', component: Error, name: 'Error' },
  {
    path: '/3ds',
    name: '3ds',
    redirect: redirectTo3DSDotCom,
    meta: { redirect: 'https://www.3ds.com/' },
  },
  {
    path: '/:pathMatch(.*)*/compass-world/',
    component: Home,
    name: 'HomeOthers',
    meta: { hasFooter: true },
  },
  { path: '/:pathMatch(.*)*', redirect: '/404' },
];

const router = createRouter({
  history: import.meta.env.SSR ? createMemoryHistory('/') : createWebHistory('/'),
  routes,
});

const computeQueryParamsToKeep = () => {
  const actualQueryParams = router.currentRoute.value.query;
  if (!actualQueryParams || !Object.keys(actualQueryParams).length) {
    return;
  }

  return Object.keys(actualQueryParams).reduce((acc: Record<string, any>, el) => {
    if (QUERY_PARAMS_TO_KEEP.includes(el)) {
      acc[el] = actualQueryParams[el];
    }

    return acc;
  }, {});
};

type urlParams = { name: string; param: string };
const setParamsInUrl = (params: urlParams[]) => {
  const formatParams = params
    .filter((el) => el.param)
    .map((param) => encodeURIComponent(param.name) + '=' + encodeURIComponent(param.param))
    .join('&');

  history.replaceState({}, '', `${location.pathname}${formatParams ? '?' : ''}${formatParams}`);
};

const removeParamInURl = (name: string) => {
  const currentParams = Object.fromEntries([...new URL(location.href).searchParams.entries()]);
  currentParams[name] = '';

  const toSet = Object.keys(currentParams).map((el) => ({ name: el, param: currentParams[el] }));
  setParamsInUrl(toSet);
};

const hasFromRoutingHaveParamsThatNeedToKeepOnToRoute = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
) => {
  const fromQueries = Object.keys(from.query);
  const toQueries = Object.keys(to.query);

  if (!fromQueries.length) {
    return true;
  }

  const queryFromToKeep = fromQueries.filter((el) => QUERY_PARAMS_TO_KEEP.includes(el));
  if (!queryFromToKeep.length) {
    return true;
  }

  return queryFromToKeep.every((el) => toQueries.includes(el));
};

const computeAddinMode = () => {
  const ADDIN_MODE_QUERY = ['solidworks', 'catiav5', 'catia3DExperience'];
  const settingsStore = useSettingsStore();
  const currentRoute = router.currentRoute.value;

  if (!currentRoute.query?.source) {
    settingsStore.setIsAddinMode(false);
  } else if (ADDIN_MODE_QUERY.includes(currentRoute.query.source as string)) {
    settingsStore.setIsAddinMode(true);
  }
};

/*
 * Send anonymous users to 3DPassport before entering a protected route.
 *
 * The me store is still empty on a hard load: fetchMe only runs from App.vue's
 * onMounted, i.e. after router.isReady(). Testing `isLoggedIn` alone therefore sends an
 * already authenticated user to 3DPassport on every refresh of a protected page, so the
 * session is resolved first — fetchMe is deduplicated, caches its result, and uses
 * noPassportRedirection, so it just resolves to undefined for anonymous users instead of
 * redirecting on its own.
 */
const redirectToPassportIfNeeded = async (to: RouteLocationNormalized) => {
  if ((!to.meta.requireAuth && !to.meta.requireOperator) || to.query.ticket) {
    return;
  }

  const me = useMeStore();
  await me.fetchMe();
  if (me.isLoggedIn) {
    return;
  }

  try {
    const passportUrl = await fetch.getServiceURL('3DPassport');
    const encodedRedirection = encodeURIComponent(
      window.location.origin + to.path + window.location.search,
    );
    window.location.assign(`${passportUrl}/login?service=${encodedRedirection}`);
  } catch (e) {
    // Never let the guard reject: an unresolved initial navigation leaves
    // router.isReady() pending and the app is never mounted (blank page).
    console.error('3DPassport redirection failed', e);
  }
};

(async () => {
  router.afterEach((to, _from) => {
    computeAddinMode();
    void tracker?.trackPage(String(to.name));
  });

  router.beforeEach(async (to, from, next) => {
    const settingsStore = useSettingsStore();
    const me = useMeStore();

    // We don't do anything server side for now => Only compute rights in ClientSide
    if (typeof window !== 'undefined') {
      /* Handle Passport Redirection in case of not logged */
      await redirectToPassportIfNeeded(to);

      if (to.meta.requireOperator) {
        await me.fetchMe();

        if (!me.isOperator) {
          next({ name: 'Home' });
          return;
        }
      }
    }

    // Use SettingsStore To Manage Meta in the App (Title / Descriptions / Optionnal values)
    settingsStore.resetMeta();
    useMetaTitle((to.meta?.title || 'Make - 3DExperience Marketplace') as string);

    Object.keys(to.meta).forEach((meta) => {
      settingsStore.setMetaOfPage(meta, to.meta[meta]);
    });

    if (!hasFromRoutingHaveParamsThatNeedToKeepOnToRoute(to, from)) {
      next({
        name: to.name as string,
        query: { ...to.query, ...computeQueryParamsToKeep() },
        params: to.params,
      });
      return;
    }

    next();
  });

  if (router.currentRoute.value.name) {
    void tracker?.trackPage(router.currentRoute.value.name as string);
  }
})();

export { router, setParamsInUrl, removeParamInURl, routes };
